import { useState, type ComponentType } from "react";
import { MessageCircle, Mail, ChevronRight, Copy, Check, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openLogfrogChat } from "../utils/logfrog";
import VideoTutorialsCard from "../components/VideoTutorialsCard";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import { isFreeTier } from "../utils/userUtils";

const SUPPORT_EMAIL = "info@dealhawkpro.io";
const DISCORD_URL = "https://discord.gg/3DTMngFYb9";

function DiscordIcon({ size = 20, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

type Row = {
  key: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  label: string;
  sub: string;
  primary?: boolean;
  onPress?: () => void;
  href?: string;
};

/**
 * Help (mirrors pinto's contact screen): chat with support first (fastest),
 * then email, then Discord for paid members.
 */
export default function ContactUsPage() {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Account id so support can find the account without asking.
  const mailto =
    `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t("emptyStates.help.emailSubject"))}` +
    (user?.userId ? `&body=${encodeURIComponent(`\n\n—\n${t("emptyStates.help.diagAccount")}: ${user.userId}`)}` : "");

  const contactRows: Row[] = [
    { key: "chat", icon: MessageCircle, label: t("emptyStates.help.chat"), sub: t("emptyStates.help.chatSub"), primary: true, onPress: openLogfrogChat },
    { key: "email", icon: Mail, label: t("emptyStates.help.emailUs"), sub: SUPPORT_EMAIL, href: mailto },
  ];
  // Help in the menu opens this screen, so the FAQ that used to have its own menu entry is a row here.
  const moreRows: Row[] = [
    ...(user && !isFreeTier(user)
      ? [{ key: "discord", icon: DiscordIcon, label: "Discord", sub: t("emptyStates.help.discordSub"), href: DISCORD_URL }]
      : []),
    { key: "faq", icon: HelpCircle, label: t("emptyStates.help.faq"), sub: t("emptyStates.help.faqSub"), onPress: () => navigate("/support") },
  ];

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const renderRow = (row: Row, isLast: boolean) => {
    const Icon = row.icon;
    const className = `flex w-full items-center gap-3.5 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-700/60 ${
      isLast ? "" : "border-b border-gray-100 dark:border-dark-700"
    }`;
    const body = (
      <>
        <span
          className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${
            row.primary
              ? "bg-primary-600 text-white"
              : "bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-purple-300"
          }`}
        >
          <Icon size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-[15px] text-gray-900 dark:text-white ${row.primary ? "font-bold" : "font-medium"}`}>{row.label}</span>
          <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-dark-400">{row.sub}</span>
        </span>
        <ChevronRight size={17} className="flex-none text-gray-400 dark:text-dark-500" aria-hidden="true" />
      </>
    );
    return row.href ? (
      <a
        key={row.key}
        href={row.href}
        className={className}
        {...(row.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {body}
      </a>
    ) : (
      <button key={row.key} type="button" onClick={row.onPress} className={className}>
        {body}
      </button>
    );
  };

  return (
    <div className="px-4 py-8 bg-gray-50 dark:bg-dark-950 min-h-full">
      <div className="max-w-sm mx-auto">
        <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
          {t("emptyStates.help.title")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-dark-400 mt-1 mb-5">
          {t("emptyStates.help.subtitle")}
        </p>

        <div className="overflow-hidden rounded-[14px] border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800">
          {contactRows.map((row, index) => renderRow(row, index === contactRows.length - 1))}
        </div>

        <div className="mt-4 overflow-hidden rounded-[14px] border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800">
          {moreRows.map((row, index) => renderRow(row, index === moreRows.length - 1))}
        </div>

        <button
          type="button"
          onClick={() => void copyEmail()}
          className={`mx-auto mt-4 flex items-center justify-center gap-1.5 py-1.5 text-[13px] font-medium ${
            copied ? "text-green-600" : "text-gray-500 hover:text-gray-700 dark:text-dark-400 dark:hover:text-dark-200"
          }`}
        >
          {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
          {copied ? t("emptyStates.help.copied") : t("emptyStates.help.copyEmail", { email: SUPPORT_EMAIL })}
        </button>

        <VideoTutorialsCard className="mt-5" />
      </div>
    </div>
  );
}
